/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Search,
  Mail,
  Send,
  CheckCircle,
  MoreHorizontal,
  Bold,
  Italic,
  Underline,
  List,
  Link as LinkIcon,
  RefreshCw,
  X,
  Users,
  ChevronDown,
  UserMinus,
  UserPlus,
  Filter,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";

const Newsletter = () => {
  const {
    newsletters,
    fetchAllNewsletters,
    unsubscribeNewsletter,
    subscribeNewsletter,
  } = useContext(AdminContext);

  // Data State
  const [subscribers, setSubscribers] = useState([]);

  // Selection State
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeMenu, setActiveMenu] = useState(null);

  // Filter State
  const [searchInput, setSearchInput] = useState(""); // What user types
  const [searchTerm, setSearchTerm] = useState(""); // What actually triggers search
  const [statusFilter, setStatusFilter] = useState("All");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Email Modal State
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Unsubscribe/Subscribe Modal State
  const [showActionModal, setShowActionModal] = useState(false);
  const [subscriberAction, setSubscriberAction] = useState(null);
  const [actionType, setActionType] = useState(""); // "unsubscribe" or "subscribe"

  // Refs for click-outside-to-close
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    fetchAllNewsletters();
  }, []);

  useEffect(() => {
    setSubscribers(
      newsletters.map((n) => ({
        id: n.id,
        name:
          n.email.split("@")[0].charAt(0).toUpperCase() +
          n.email.split("@")[0].slice(1),
        initial:
          n.name?.charAt(0)?.toUpperCase() || n.email.charAt(0).toUpperCase(),
        email: n.email,
        status: n.status === "subscribed" ? "Subscribed" : "Unsubscribed",
        tags: ["Newsletter"],
        subscribedDate: new Date(n.subscribedAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        isSubscribed: n.status === "subscribed",
      })),
    );
  }, [newsletters]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu-container")) return;
      setActiveMenu(null);

      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Filter
  const filteredSubscribers = subscribers.filter((sub) => {
    const matchesSearch =
      sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || sub.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const paginatedItems = filteredSubscribers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPages = Math.ceil(filteredSubscribers.length / itemsPerPage);

  // Reset pagination on filters
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  // Selection Handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const pageIds = paginatedItems.map((s) => s.id);
      setSelectedIds((prev) => new Set([...prev, ...pageIds]));
    } else {
      const pageIds = new Set(paginatedItems.map((s) => s.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of pageIds) next.delete(id);
        return next;
      });
    }
  };

  const handleSelectOne = (id, e) => {
    if (e) {
      e.stopPropagation();
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isAllPageSelected =
    paginatedItems.length > 0 &&
    paginatedItems.every((s) => selectedIds.has(s.id));

  // Email Logic
  const handleOpenEmailModal = () => {
    if (selectedIds.size === 0) return;
    setEmailSubject("");
    setEmailBody("");
    setEmailError("");
    setShowEmailModal(true);
  };

  const handleSendEmail = () => {
    if (!emailSubject.trim()) {
      setEmailError("Subject is required.");
      return;
    }
    if (!emailBody.trim()) {
      setEmailError("Content is required.");
      return;
    }

    setIsSending(true);
    // Simulate API call
    setTimeout(() => {
      setIsSending(false);
      setShowEmailModal(false);
      setShowSuccess(true);
      setSelectedIds(new Set());
      setTimeout(() => setShowSuccess(false), 3000);
    }, 1500);
  };

  //  Unsubscribe/Subscribe Logic
  const initiateAction = (sub, type) => {
    setSubscriberAction(sub);
    setActionType(type);
    setShowActionModal(true);
    setActiveMenu(null);
  };

  const confirmAction = async () => {
    if (!subscriberAction) return;

    try {
      if (actionType === "unsubscribe") {
        await unsubscribeNewsletter(subscriberAction.id);
        // Update local state instead of refetching
        setSubscribers((prev) =>
          prev.map((sub) =>
            sub.id === subscriberAction.id
              ? { ...sub, status: "Unsubscribed", isSubscribed: false }
              : sub,
          ),
        );
      } else if (actionType === "subscribe") {
        await subscribeNewsletter(subscriberAction.email);
        // Update local state instead of refetching
        setSubscribers((prev) =>
          prev.map((sub) =>
            sub.email === subscriberAction.email
              ? { ...sub, status: "Subscribed", isSubscribed: true }
              : sub,
          ),
        );
      }

      setShowActionModal(false);
      setSubscriberAction(null);
      setActionType("");
    } catch (error) {
      toast.error("Action failed");
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setShowStatusDropdown(false);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearchTerm("");
    setStatusFilter("All");
    // No need to refetch - just reset filters, data is already in newsletters state
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Subscribed":
        return "bg-primary text-white  border border-primary";
      case "Unsubscribed":
        return "bg-red-600 text-white border border-red-800";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };

  const getInitialColor = (initial) => {
    const colors = [
      "bg-blue-500",
      "bg-emerald-500",
      "bg-purple-500",
      "bg-amber-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-cyan-500",
      "bg-orange-500",
    ];
    const index = initial.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 relative">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Newsletter
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            Manage subscriptions and communicate with your audience.
          </p>
        </div>

        {/* Stats */}
        <div className="flex gap-3 sm:gap-4">
          <div className="bg-card dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex items-center gap-3 flex-1 sm:flex-none">
            <div className="bg-gray-200 dark:bg-slate-800 p-2 rounded-xl text-primary">
              <Users size={18} className="sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-dark-muted">
                Total Subscribers
              </p>
              <p className="text-lg font-bold text-text dark:text-dark-text">
                {subscribers.filter((s) => s.status === "Subscribed").length}
              </p>
            </div>
          </div>
          <div className="bg-card dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex items-center gap-3 flex-1 sm:flex-none">
            <div className="bg-gray-200 dark:bg-slate-800 p-2 rounded-xl text-primary">
              <Send size={18} className="sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-dark-muted">
                Emails Sent
              </p>
              <p className="text-lg font-bold text-text dark:text-dark-text">
                85k
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Notification */}
      {showSuccess && (
        <div className="fixed top-20 sm:top-24 right-4 sm:right-8 bg-emerald-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl shadow-lg z-50 animate-in slide-in-from-right fade-in duration-300 flex items-center gap-2 text-sm sm:text-base">
          <CheckCircle size={18} className="sm:w-5 sm:h-5" />
          <span className="font-bold">Campaign Sent Successfully!</span>
        </div>
      )}

      {/* Filter Bar  */}
      <div className="bg-card dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6">
        <div className="flex flex-col lg:flex-row gap-2">
          {/* Search Bar */}
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
                size={16}
              />
              <input
                type="text"
                placeholder="Search subscribers by name or email..."
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text placeholder-gray-400"
              />
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearchSubmit}
              className="flex items-center justify-center px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <Search size={16} />
            </button>

            {/* Status Filter Dropdown */}
            <div className="relative" ref={statusDropdownRef}>
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
              >
                <Filter size={16} />
                {statusFilter === "All" ? "All Statuses" : statusFilter}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${
                    showStatusDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showStatusDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  {["All", "Subscribed", "Unsubscribed"].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusFilter(status)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === status
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {status === "All" ? "All Statuses" : status}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="flex items-center justify-center px-4 py-2 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        {/* Selection Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 border-b border-primary/20 dark:border-primary/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-primary dark:text-primary">
                {selectedIds.size} subscriber(s) selected
              </span>
            </div>
            <button
              onClick={handleOpenEmailModal}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors flex items-center gap-2 text-sm shadow-lg shadow-primary/20 dark:shadow-none"
            >
              <Mail size={16} /> Send Email
            </button>
          </div>
        )}

        {/* Table  */}
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-sm text-left min-w-[800px]">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th className="px-4 sm:px-6 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={isAllPageSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 cursor-pointer"
                  />
                </th>
                <th className="px-4 sm:px-6 py-3">Subscriber</th>
                <th className="px-4 sm:px-6 py-3">Status</th>
                <th className="px-4 sm:px-6 py-3">Joined Date</th>
                <th className="px-4 sm:px-6 py-3">Tags</th>
                <th className="px-4 sm:px-6 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paginatedItems.map((sub) => (
                <tr
                  key={sub.id}
                  className={`hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors ${
                    selectedIds.has(sub.id)
                      ? "bg-primary/5 dark:bg-primary/10"
                      : ""
                  }`}
                >
                  <td
                    className="px-4 sm:px-6 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(sub.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectOne(sub.id, e);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <div className="flex items-center gap-3">
                      {/* Single letter avatar */}
                      <div
                        className={`${getInitialColor(
                          sub.initial,
                        )} w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white font-bold text-sm`}
                      >
                        {sub.initial}
                      </div>
                      <div>
                        <p className="font-bold text-text dark:text-dark-text text-sm sm:text-base">
                          {sub.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-dark-muted truncate max-w-[150px] sm:max-w-none">
                          {sub.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusBadge(
                        sub.status,
                      )}`}
                    >
                      {sub.status}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-gray-500 dark:text-dark-muted text-sm">
                    {sub.subscribedDate}
                  </td>
                  <td className="px-4 sm:px-6 py-3">
                    <div className="flex flex-wrap gap-1">
                      {sub.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-600 dark:text-gray-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-center relative action-menu-container">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === sub.id ? null : sub.id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <MoreHorizontal size={18} />
                    </button>

                    {/* Action Menu */}
                    {activeMenu === sub.id && (
                      <div className="absolute right-2 sm:right-8 top-8 w-36 sm:w-44 bg-white dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                        {sub.status === "Subscribed" ? (
                          <button
                            onClick={() => initiateAction(sub, "unsubscribe")}
                            className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <UserMinus size={14} className="sm:w-4 sm:h-4" />{" "}
                            Unsubscribe
                          </button>
                        ) : (
                          <button
                            onClick={() => initiateAction(sub, "subscribe")}
                            className="w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2"
                          >
                            <UserPlus size={14} className="sm:w-4 sm:h-4" />{" "}
                            Subscribe
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {paginatedItems.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    No subscribers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      {/* Email Composer Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl animate-in fade-in zoom-in-95 duration-200 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
                <Mail size={20} className="text-primary" /> Compose Newsletter
              </h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 flex-1 overflow-y-auto">
              {/* Recipient Info */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 dark:text-dark-muted font-medium">
                  To:
                </span>
                <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full text-xs font-bold">
                  {selectedIds.size} Recipients Selected
                </span>
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-4 py-3 text-lg font-bold border-b border-gray-200 dark:border-gray-700 bg-transparent outline-none placeholder-gray-400 dark:text-dark-text focus:border-primary transition-colors"
                />
              </div>

              {/* Rich Text Toolbar Mock */}
              <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 pb-2 overflow-x-auto">
                {[Bold, Italic, Underline, List, LinkIcon].map((Icon, i) => (
                  <button
                    key={i}
                    className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors flex-shrink-0"
                  >
                    <Icon size={16} />
                  </button>
                ))}
              </div>

              {/* Editor Area */}
              <textarea
                className="w-full h-48 sm:h-64 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/20 resize-none text-text dark:text-dark-text leading-relaxed"
                placeholder="Write your amazing newsletter content here..."
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />

              {emailError && (
                <p className="text-red-500 text-sm font-medium animate-in fade-in">
                  {emailError}
                </p>
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 sm:px-6 py-2.5 bg-white border border-gray-200 dark:bg-gray-800 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
              >
                Discard
              </button>
              <button
                onClick={handleSendEmail}
                disabled={isSending}
                className="px-6 sm:px-8 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {isSending ? (
                  <>Sending...</>
                ) : (
                  <>
                    Send Campaign <Send size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal (Unsubscribe/Subscribe) */}
      {showActionModal && subscriberAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6 text-center">
            <div
              className={`w-12 h-12 rounded-full ${
                actionType === "unsubscribe"
                  ? "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                  : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
              } flex items-center justify-center mx-auto mb-4`}
            >
              {actionType === "unsubscribe" ? (
                <UserMinus size={24} />
              ) : (
                <UserPlus size={24} />
              )}
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-text dark:text-dark-text mb-2">
              {actionType === "unsubscribe"
                ? "Unsubscribe User?"
                : "Subscribe User?"}
            </h3>
            <p className="text-gray-500 dark:text-dark-muted mb-6 text-sm">
              {actionType === "unsubscribe"
                ? `Are you sure you want to unsubscribe ${subscriberAction.email}? They will no longer receive newsletters.`
                : `Are you sure you want to subscribe ${subscriberAction.email}? They will start receiving newsletters.`}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setSubscriberAction(null);
                  setActionType("");
                }}
                className="flex-1 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-text dark:text-dark-text font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                className={`flex-1 px-4 py-2 rounded-xl text-white font-medium transition-colors shadow-lg ${
                  actionType === "unsubscribe"
                    ? "bg-red-600 hover:bg-red-700 shadow-red-200 dark:shadow-none"
                    : "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200 dark:shadow-none"
                } text-sm sm:text-base`}
              >
                {actionType === "unsubscribe" ? "Unsubscribe" : "Subscribe"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Newsletter;
